import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mammoth from 'mammoth';
import { useTranslation } from 'react-i18next';
import apis from '../../utils/apis';
import PDFViewer from '../Files/documents/PDFViewer';
import {
  MAX_PREVIEW_COLUMNS,
  MAX_PREVIEW_ROWS,
  WorkbookPreview,
  columnLabel,
  isSpreadsheetExtension,
  toWorkbookPreview,
} from './spreadsheetPreview';

interface CustomFileReaderProps {
  fileUrl?: string;
  content?: string;
  isPdf?: boolean;
  filename: string;
}

/**
 * Helper: fetch a URL as a specific responseType.
 * Uses native fetch() for blob: URLs (axios prepends baseURL to them,
 * breaking the request) and falls back to the configured apis instance
 * for normal API paths.
 */
async function fetchUrl(url: string, responseType: 'blob' | 'arraybuffer' | 'text') {
  const isBlobUrl = url.startsWith('blob:');

  if (isBlobUrl) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch blob URL: ${res.statusText}`);
    const contentType = res.headers.get('content-type') || 'application/octet-stream';

    if (responseType === 'arraybuffer') {
      return { data: await res.arrayBuffer(), contentType };
    } else if (responseType === 'blob') {
      return { data: await res.blob(), contentType };
    } else {
      return { data: await res.text(), contentType };
    }
  }

  // Regular API path - use configured axios instance
  const response = await apis.get(url, { responseType });
  return {
    data: response.data,
    contentType: (response.headers?.['content-type'] as string) || 'application/octet-stream',
  };
}

/**
 * A workbook as a grid: sheet tabs, column letters, row numbers.
 *
 * Deliberately a plain table rather than the platform `Table` component —
 * this is a spreadsheet, so the cells are positional, the header is A/B/C
 * rather than a field name, and there is nothing to sort or filter. Borrowing
 * a data table would mean pretending a sheet has columns that mean something.
 *
 * `overflow-auto` on its own box, with a sticky header row and a sticky row
 * gutter, so a wide sheet scrolls inside the reader instead of widening it.
 */
const SheetGrid: React.FC<{
  workbook: WorkbookPreview;
  active: number;
  onSelectSheet: (index: number) => void;
  t: (key: string, options?: any) => any;
}> = ({ workbook, active, onSelectSheet, t }) => {
  const sheet = workbook.sheets[Math.min(active, workbook.sheets.length - 1)];
  if (!sheet) return null;
  const width = sheet.rows.reduce((widest, row) => Math.max(widest, row.length), 0);

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="sheet-grid">
      {/* Only when there is a choice to make. */}
      {workbook.sheets.length > 1 && (
        <div className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 border-b border-hairline-soft
                        overflow-x-auto custom-scrollbar-main">
          {workbook.sheets.map((entry, index) => (
            <button
              key={entry.name}
              type="button"
              onClick={() => onSelectSheet(index)}
              data-testid={`sheet-tab-${index}`}
              aria-current={index === active ? 'true' : undefined}
              className={
                index === active
                  ? 'flex-shrink-0 px-2.5 py-1 rounded-md text-xs font-medium bg-champagne/20 text-fg-strong'
                  : 'flex-shrink-0 px-2.5 py-1 rounded-md text-xs text-fg-muted hover:text-fg-strong transition-colors duration-200'
              }
            >
              {entry.name}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-main">
        {sheet.rows.length === 0 ? (
          <div className="flex items-center justify-center h-full p-8">
            <p className="text-sm text-fg-muted">{t('This sheet is empty.')}</p>
          </div>
        ) : (
          <table className="border-collapse text-xs" data-testid="sheet-table">
            <thead>
              <tr>
                <th className="sticky top-0 start-0 z-20 bg-surface-sunken border border-hairline-soft
                               px-2 py-1 w-10" />
                {Array.from({ length: width }, (_, column) => (
                  <th
                    key={column}
                    className="sticky top-0 z-10 bg-surface-sunken border border-hairline-soft
                               px-2 py-1 text-[10px] font-medium text-fg-muted text-start min-w-[6rem]"
                  >
                    {columnLabel(column)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sheet.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td className="sticky start-0 z-10 bg-surface-sunken border border-hairline-soft
                                 px-2 py-1 text-[10px] text-fg-muted text-end tabular-nums">
                    {rowIndex + 1}
                  </td>
                  {Array.from({ length: width }, (_, column) => (
                    <td
                      key={column}
                      className="border border-hairline-soft px-2 py-1 text-fg-body
                                 whitespace-nowrap max-w-[20rem] truncate"
                      title={row[column] || undefined}
                    >
                      {row[column]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Say what is not being shown, rather than letting it look complete. */}
      {sheet.truncated && (
        <p className="flex-shrink-0 px-3 py-1.5 border-t border-hairline-soft text-[11px] text-fg-muted"
           data-testid="sheet-truncated">
          {t('Showing the first {{rows}} rows and {{columns}} columns of {{totalRows}} × {{totalColumns}}. Download the file to see all of it.', {
            rows: Math.min(sheet.totalRows, MAX_PREVIEW_ROWS),
            columns: Math.min(sheet.totalColumns, MAX_PREVIEW_COLUMNS),
            totalRows: sheet.totalRows,
            totalColumns: sheet.totalColumns,
          })}
        </p>
      )}
    </div>
  );
};

const CustomFileReader: React.FC<CustomFileReaderProps> = ({ fileUrl, content, isPdf, filename }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [fetchedContent, setFetchedContent] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isHtmlContent, setIsHtmlContent] = useState(false);
  /** A parsed .xlsx, ready to render as a grid. */
  const [workbook, setWorkbook] = useState<WorkbookPreview | null>(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const blobUrlRef = useRef<string | null>(null);

  // Determine file type from filename
  const ext = (filename || '').split('.').pop()?.toLowerCase() || '';
  const isFilePdf = isPdf || ext === 'pdf';
  const isDocx = ext === 'docx';
  const isDoc = ext === 'doc';
  /* A spreadsheet is READ, not just downloaded - see the branch below. The
     remaining Office formats have no in-app renderer and still fall through. */
  const isSpreadsheet = isSpreadsheetExtension(ext);
  const isOfficeDoc = ['ppt', 'pptx'].includes(ext);
  const isPlainText = ['txt', 'md', 'csv', 'json', 'xml', 'log'].includes(ext);
  // Browser-renderable image formats - previewed inline via <img>. Non-web
  // formats (tiff/tif) fall through to the download fallback below.
  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext);

  useEffect(() => {
    // If content is provided directly, no need to fetch
    if (content) {
      setIsLoading(false);
      return;
    }

    if (!fileUrl) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setBlobUrl(null);
    setFetchedContent(null);
    setFetchError(null);
    setIsHtmlContent(false);
    setWorkbook(null);
    setActiveSheet(0);

    const fetchFile = async () => {
      try {
        if (isFilePdf) {
          // PDFs: fetch as blob, create object URL for PDFViewer
          const { data } = await fetchUrl(fileUrl, 'blob');
          const blob = new Blob([data as Blob], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          blobUrlRef.current = url;
          setBlobUrl(url);
        } else if (isDocx) {
          // DOCX: fetch as arraybuffer, convert to HTML via mammoth
          const { data } = await fetchUrl(fileUrl, 'arraybuffer');
          const arrayBuffer = data as ArrayBuffer;
          try {
            const result = await mammoth.convertToHtml({ arrayBuffer });
            if (result.value) {
              setFetchedContent(result.value);
              setIsHtmlContent(true);
            } else {
              setFetchError(t('Document appears to be empty.'));
            }
          } catch (mammothErr) {
            console.error('Mammoth conversion error:', mammothErr);
            setFetchError(t('Failed to convert document. The file may be corrupted.'));
          }
        } else if (isDoc) {
          // Legacy .doc: show download prompt (mammoth doesn't support .doc)
          const { data, contentType: ct } = await fetchUrl(fileUrl, 'blob');
          const blob = new Blob([data as Blob], {
            type: ct || 'application/msword',
          });
          const url = URL.createObjectURL(blob);
          blobUrlRef.current = url;
          setBlobUrl(url);
        } else if (isSpreadsheet) {
          /*
            XLSX, RENDERED. This used to fall through to the download-only
            card, so a schedule attached to a task could not be looked at
            without leaving the app.

            `raw: false` asks SheetJS for the FORMATTED value, which is what
            makes a date read as a date and a currency cell read as money
            rather than as the serial number underneath it. `header: 1` gives
            a plain grid rather than objects keyed on the first row - a
            preview has no idea whether row one is a header, and guessing
            wrong hides a row of data.

            The library is imported here rather than at the top: it is a large
            dependency that only a spreadsheet needs, and the other file types
            should not carry it.
          */
          const { data } = await fetchUrl(fileUrl, 'arraybuffer');
          try {
            const XLSX = await import('@e965/xlsx');
            const book = XLSX.read(data as ArrayBuffer, { type: 'array', cellDates: true });
            const preview = toWorkbookPreview(book.SheetNames || [], (name) =>
              XLSX.utils.sheet_to_json(book.Sheets[name], {
                header: 1,
                raw: false,
                defval: '',
                blankrows: false,
              }) as unknown[][]
            );
            if (preview.sheets.length) {
              setWorkbook(preview);
            } else {
              setFetchError(t('This spreadsheet appears to be empty.'));
            }
          } catch (sheetErr) {
            console.error('Spreadsheet parse error:', sheetErr);
            /* Fall back to the download card rather than an error page: the
               bytes are fine, we just could not read them. */
            const blob = new Blob([data as ArrayBuffer]);
            const url = URL.createObjectURL(blob);
            blobUrlRef.current = url;
            setBlobUrl(url);
          }
        } else if (isPlainText) {
          // Plain text files: fetch as text
          const { data } = await fetchUrl(fileUrl, 'text');
          setFetchedContent(typeof data === 'string' ? data : JSON.stringify(data, null, 2));
        } else if (isOfficeDoc) {
          // Other Office docs: fetch as blob and create object URL
          const { data, contentType: ct } = await fetchUrl(fileUrl, 'blob');
          const blob = new Blob([data as Blob], {
            type: ct || 'application/octet-stream',
          });
          const url = URL.createObjectURL(blob);
          blobUrlRef.current = url;
          setBlobUrl(url);
        } else {
          // Other types: try to fetch as blob and create object URL
          const { data, contentType } = await fetchUrl(fileUrl, 'blob');
          const blob = data instanceof Blob ? data : new Blob([data], { type: contentType });

          // If content type is text-like, read it as text
          if (contentType.startsWith('text/') || contentType.includes('json') || contentType.includes('xml')) {
            const text = await blob.text();
            setFetchedContent(text);
          } else {
            const url = URL.createObjectURL(blob);
            blobUrlRef.current = url;
            setBlobUrl(url);
          }
        }
      } catch (error) {
        console.error('Error fetching file:', error);
        setFetchError(t('Failed to load file. Please try again.'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchFile();

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [fileUrl, content, isFilePdf, isDocx, isDoc, isSpreadsheet, isOfficeDoc, isPlainText]);

  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-white dark:bg-gray-800">
        {/* Toolbar skeleton */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="h-4 w-4 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse" />
          <div className="h-3 w-32 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse" />
          <div className="flex-1" />
          <div className="h-3 w-16 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse" />
        </div>

        {/* Document body skeleton */}
        <div className="flex-1 overflow-hidden px-10 py-8 space-y-6 animate-fadePulse">
          {/* Title area */}
          <div className="space-y-2">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/5" />
            <div className="h-3 bg-gray-100 dark:bg-gray-750 rounded w-1/4" />
          </div>

          {/* Paragraph 1 */}
          <div className="space-y-2.5 pt-2">
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-11/12" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-4/5" />
          </div>

          {/* Paragraph 2 */}
          <div className="space-y-2.5 pt-2">
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
          </div>

          {/* Subheading */}
          <div className="pt-3">
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-2/5" />
          </div>

          {/* Paragraph 3 */}
          <div className="space-y-2.5">
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
          </div>

          {/* Paragraph 4 */}
          <div className="space-y-2.5 pt-2">
            <div className="h-3.5 bg-gray-100 dark:bg-gray-750 rounded w-full" />
            <div className="h-3.5 bg-gray-100 dark:bg-gray-750 rounded w-full" />
            <div className="h-3.5 bg-gray-100 dark:bg-gray-750 rounded w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <p className="text-danger-500 text-sm">{fetchError}</p>
      </div>
    );
  }

  // Resolve content: prefer prop, then fetched
  const displayContent = content || fetchedContent;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800">
      <div className="flex-1 overflow-y-auto">
        {isFilePdf ? (
          blobUrl ? (
            <PDFViewer pdfUrl={blobUrl} />
          ) : (
            <p className="text-danger-500 p-6">{t('Failed to load PDF. Please try again or check the URL.')}</p>
          )
        ) : isHtmlContent && displayContent ? (
          <div className="w-full">
            <div 
              className="prose dark:prose-invert max-w-none px-10 py-8"
              style={{
                fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                lineHeight: 1.7,
                color: '#1a1a1a',
                fontSize: '15px',
              }}
              dangerouslySetInnerHTML={{ __html: displayContent }}
            />
          </div>
        ) : workbook ? (
          <SheetGrid
            workbook={workbook}
            active={activeSheet}
            onSelectSheet={setActiveSheet}
            t={t}
          />
        ) : displayContent && (displayContent.includes('<!DOCTYPE') || displayContent.includes('<html') || displayContent.includes('<body')) ? (
          <iframe
            title={filename || 'Document'}
            srcDoc={displayContent}
            className="w-full h-full border-0"
            style={{ minHeight: '80vh' }}
            sandbox="allow-same-origin"
          />
        ) : displayContent ? (
          <div className="prose dark:prose-invert max-w-none px-10 py-8">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {displayContent}
            </ReactMarkdown>
          </div>
        ) : isImage && blobUrl ? (
          <div className="flex items-center justify-center min-h-full p-6">
            <img
              src={blobUrl}
              alt={filename}
              className="max-w-full max-h-[80vh] h-auto object-contain rounded"
            />
          </div>
        ) : blobUrl ? (
          <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('Preview is not available for this file type.')}
            </p>
            <a
              href={blobUrl}
              download={filename}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-info-600 dark:text-info-400 bg-info-50 dark:bg-info-900/20 rounded-lg hover:bg-info-100 dark:hover:bg-info-900/30 transition-colors"
            >
              {t('Download')} {filename}
            </a>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full p-8">
            <p className="text-sm text-gray-400 dark:text-gray-500">{t('No content available')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomFileReader;